/**
 * Shared API error catalog.
 *
 * The worker throws these definitions. The web app compares `response.code`
 * with `apiErrors.*.code` instead of matching English messages.
 */

export type ApiErrorDefinition = {
  status: number;
  code: string;
  message: string;
};

export const apiErrors = {
  validationFailed: {
    status: 400,
    code: "validation_failed",
    message: "Validation failed",
  },
  internalError: {
    status: 500,
    code: "internal_error",
    message: "Something went wrong",
  },
  authenticationFailed: {
    status: 401,
    code: "authentication_failed",
    message: "Authentication failed",
  },
  authenticationRequired: {
    status: 401,
    code: "authentication_required",
    message: "Authentication required",
  },
  forbidden: {
    status: 403,
    code: "forbidden",
    message: "Insufficient permissions",
  },

  userNotFound: {
    status: 404,
    code: "user_not_found",
    message: "User not found",
  },
  noRefreshToken: {
    status: 401,
    code: "no_refresh_token",
    message: "Refresh token required",
  },
  invalidRefreshToken: {
    status: 401,
    code: "invalid_refresh_token",
    message: "Invalid or expired refresh token",
  },
  userInactive: {
    status: 401,
    code: "user_inactive",
    message: "User inactive",
  },
  accountLocked: {
    status: 403,
    code: "account_locked",
    message: "Account locked",
  },
  noAccessToken: {
    status: 401,
    code: "no_access_token",
    message: "No access token",
  },
  invalidToken: {
    status: 401,
    code: "invalid_token",
    message: "Invalid token",
  },
  tokenExpired: {
    status: 401,
    code: "token_expired",
    message: "Token expired",
  },
  sessionNotFound: {
    status: 401,
    code: "session_not_found",
    message: "Session not found",
  },
  sessionExpired: {
    status: 401,
    code: "session_expired",
    message: "Session expired",
  },
  sessionInactive: {
    status: 401,
    code: "session_inactive",
    message: "Session is not active",
  },
  rateLimited: {
    status: 403,
    code: "rate_limited",
    message: "Rate limited",
  },
  emailExists: {
    status: 400,
    code: "email_exists",
    message: "Email exists",
  },
  weakPassword: {
    status: 400,
    code: "weak_password",
    message: "Password does not meet security requirements",
  },
  creationFailed: {
    status: 400,
    code: "creation_failed",
    message: "Failed to create user account",
  },
  invalidBody: {
    status: 400,
    code: "invalid_body",
    message: "Email and password are required",
  },
  tooManyFailedAttempts: {
    status: 400,
    code: "too_many_failed_attempts",
    message: "Too many failed attempts. Please try again later.",
  },
  invalidCredentials: {
    status: 401,
    code: "invalid_credentials",
    message: "Invalid credentials.",
  },
  googleNotConfigured: {
    status: 503,
    code: "google_not_configured",
    message: "Google login is not configured",
  },
  invalidGoogleCode: {
    status: 400,
    code: "invalid_google_code",
    message: "Invalid or expired Google authorization code",
  },
  invalidGoogleToken: {
    status: 400,
    code: "invalid_google_token",
    message: "Google token did not include an email address",
  },
  userAlreadyExists: {
    status: 400,
    code: "user_already_exists",
    message: "User already exists.",
  },
  accountNotFound: {
    status: 404,
    code: "account_not_found",
    message: "Account not found",
  },
  invalidCode: {
    status: 400,
    code: "invalid_code",
    message: "Invalid verification code",
  },
  codeExpired: {
    status: 400,
    code: "code_expired",
    message: "Code expired or missing. Request a new code.",
  },
  invalidOrExpiredToken: {
    status: 400,
    code: "invalid_or_expired_token",
    message: "Invalid or expired verification token",
  },
  emailMismatch: {
    status: 400,
    code: "email_mismatch",
    message: "Email has been changed since verification was requested",
  },
  emailAlreadyExists: {
    status: 409,
    code: "email_already_exists",
    message: "This email is no longer available",
  },
  failedToUpdateEmail: {
    status: 500,
    code: "failed_to_update_email",
    message: "Failed to update email",
  },
  missingToken: {
    status: 400,
    code: "missing_token",
    message: "Missing token",
  },
  invalidResetToken: {
    status: 400,
    code: "invalid_reset_token",
    message: "Invalid or expired token",
  },
  resetFailed: {
    status: 500,
    code: "reset_failed",
    message: "Failed to reset password",
  },
  emailUnavailable: {
    status: 400,
    code: "email_unavailable",
    message: "Email has already been used",
  },
  failedToSendSms: {
    status: 400,
    code: "failed_to_send_sms",
    message: "Failed to send sms code. Please try again.",
  },
  smsCodeInvalid: {
    status: 400,
    code: "sms_code_is_invalid",
    message: "SMS code is invalid. Please try again.",
  },
  phoneUnavailable: {
    status: 400,
    code: "phone_unavailable",
    message: "Phone number has already been used",
  },
} as const satisfies Record<string, ApiErrorDefinition>;

export type ApiErrorKey = keyof typeof apiErrors;
export type ApiErrorCode = (typeof apiErrors)[ApiErrorKey]["code"];

const apiErrorCodes = new Set<string>(
  Object.values(apiErrors).map((error) => error.code),
);

export const isApiErrorCode = (code: string): code is ApiErrorCode =>
  apiErrorCodes.has(code);
